import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import isObject from 'lodash/isObject';
import isEmpty from 'lodash/isEmpty';
import * as types from '../actions/types';
import { updateManagedSoundcastsStatus, receiveManagedSoundcastEpisodes } from '../actions';
import { getManagedSoundcast } from '../selectors';
import {
  deleteManagedSoundcastRatings,
  fetchManagedSoundcastEpisodes,
  fetchManagedSoundcastRatingsList,
  fetchManagedSoundcastRatingsOverview,
  updateManagedSoundcastsOrder,
  updateManagedBundlesOrder,
  updateManagedBundleSouncastsOrder,
  fetchManagedSoundcastsOrder,
  fetchManagedBundlesOrder,
  fetchManagedBundleSoundcastsOrder,
} from '../routines';
import {
  getSoundcastRatings,
  getSoundcastRatingsOverview,
  getSoundcastsEpisodes,
  updateSoundcastsOrder,
  updateBundleSoundcastsOrder,
  getSoundcastsOrder,
  getBundleSoundcastsOrder,
} from '../managers';
import { changeSoundcastsOrder, changeBundlesOrder, changeBundleSoundcastsOrder } from '../actions';
const { assignEpisodeOldProps } = require('../../server/scripts/dataTransition');

const soundcastUpdateWorker = function* () {
  // get soundcasts managed
  const { byId, allId, complete: oldComplete } = yield select(getManagedSoundcast);
  const complete = allId.every(soundcastId => isObject(byId[soundcastId]));
  if (!oldComplete && complete) {
    yield put(updateManagedSoundcastsStatus('complete'));
  }
};

const soundcastUpdateWatcher = function* () {
  yield takeLatest(types.UPDATE_MANAGED_SOUNDCAST, soundcastUpdateWorker);
};

const fetchSoundcastEpisodesWorker = function* ({ payload: { soundcast, page = 1, offset = -1 } }) {
  const { soundcastId } = soundcast;
  const payload = { soundcastId, page, offset };
  try {
    yield put(fetchManagedSoundcastEpisodes.request(payload));

    const { data: source, total } = yield call(getSoundcastsEpisodes, payload);
    const episodes = source.reduce((prev, episode) => {
      // TODO: remove assigning old props when everything is transitioned
      prev[episode.episodeId] = assignEpisodeOldProps(episode);
      return prev;
    }, {});

    yield put(
      receiveManagedSoundcastEpisodes({
        soundcastId,
        total,
        episodes,
      })
    );

    yield put(fetchManagedSoundcastEpisodes.success(payload));
  } catch (error) {
    yield put(fetchManagedSoundcastEpisodes.failure(payload, error));
  } finally {
    yield put(fetchManagedSoundcastEpisodes.fulfill(payload));
  }
};

const fetchSoundcastEpisodesWatcher = function* () {
  yield takeLatest(fetchManagedSoundcastEpisodes.TRIGGER, fetchSoundcastEpisodesWorker);
};

const fetchSoundcastRatingsListWorker = function* ({ payload: { soundcastId, page = 1 } }) {
  const payload = { soundcastId };
  try {
    yield put(fetchManagedSoundcastRatingsList.request(payload));

    const { data: source = [], total = 0 } = yield call(getSoundcastRatings, soundcastId, page);
    const ratings = source.reduce((prev, rating) => {
      prev[rating.ratingId] = rating;
      return prev;
    }, {});

    yield put(
      fetchManagedSoundcastRatingsList.success({
        ...payload,
        ratings,
        total,
      })
    );
  } catch (error) {
    yield put(fetchManagedSoundcastRatingsList.failure(payload, error));
  } finally {
    yield put(fetchManagedSoundcastRatingsList.fulfill(payload));
  }
};

const fetchSoundcastRatingsListWatcher = function* () {
  yield takeLatest(fetchManagedSoundcastRatingsList.TRIGGER, fetchSoundcastRatingsListWorker);
};

const fetchSoundcastRatingsOverviewWorker = function* ({ payload: { soundcastId } }) {
  const payload = { soundcastId };
  try {
    yield put(fetchManagedSoundcastRatingsOverview.request(payload));

    const { data } = yield call(getSoundcastRatingsOverview, soundcastId);
    yield put(
      fetchManagedSoundcastRatingsOverview.success({
        ...payload,
        ...data,
      })
    );
  } catch (error) {
    yield put(fetchManagedSoundcastRatingsOverview.failure(payload, error));
  } finally {
    yield put(fetchManagedSoundcastRatingsOverview.fulfill(payload));
  }
};

const fetchSoundcastRatingsOverviewWatcher = function* () {
  yield takeLatest(
    fetchManagedSoundcastRatingsOverview.TRIGGER,
    fetchSoundcastRatingsOverviewWorker
  );
};

const deletedSoundcastRatingsWatcher = function* () {
  yield takeLatest(deleteManagedSoundcastRatings.SUCCESS, fetchSoundcastRatingsOverviewWorker);
};

const updateSoundcastsOrderWorker = function* ({ payload }) {
  const { publisherID, soundcasts } = payload;
  try {
    const orderedIds = soundcasts.map(value => value.soundcastId);
    yield put(changeSoundcastsOrder({ orderedIds }));

    yield call(updateSoundcastsOrder, publisherID, soundcasts);
  } catch (error) {
    console.log(`ERROR: update soundcasts order error: ${error.message}`);
  }
};

const updateSoundcastsOrderWatcher = function* () {
  yield takeLatest(updateManagedSoundcastsOrder.TRIGGER, updateSoundcastsOrderWorker);
};

const updateBundlesOrderWorker = function* ({ payload }) {
  const { publisherID, soundcasts } = payload;
  try {
    const orderedIds = soundcasts.map(value => value.soundcastId);
    yield put(changeBundlesOrder({ orderedIds }));

    yield call(updateSoundcastsOrder, publisherID, soundcasts);
  } catch (error) {
    console.log(`ERROR: update bundles order error: ${error.message}`);
  }
};

const updateBundlesOrderWatcher = function* () {
  yield takeLatest(updateManagedBundlesOrder.TRIGGER, updateBundlesOrderWorker);
};

const updateBundleSoundcastsOrderWorker = function* ({ payload }) {
  const { soundcastId, soundcasts, selectedItems = [], isTemp = false, order = {} } = payload;

  if (isEmpty(soundcastId)) {
    return;
  }

  try {
    const soundcastIdTempKey = `${soundcastId}_temp`;

    if (isTemp) {
      let orderedSelectedSoundcastIds = [];
      soundcasts.forEach(orderedItem => {
        const soundcast = selectedItems.find(item => item.id === orderedItem.id);
        if (!isEmpty(soundcast)) {
          orderedSelectedSoundcastIds.push(soundcast.id);
        }
      });

      const orderedInBundles = {};
      orderedInBundles[soundcastIdTempKey] = orderedSelectedSoundcastIds;

      yield put(changeBundleSoundcastsOrder(orderedInBundles));
    } else {
      if (isEmpty(order)) {
        return;
      }

      const { dashboardBundleSoundcastIds: orderedInBundles = {} } = order;
      if (
        isEmpty(orderedInBundles) ||
        !Object.keys(orderedInBundles).includes(soundcastIdTempKey)
      ) {
        return;
      }

      const orderedIds = orderedInBundles[soundcastIdTempKey];
      delete orderedInBundles[soundcastIdTempKey];
      orderedInBundles[soundcastId] = orderedIds;

      yield put(changeBundleSoundcastsOrder(orderedInBundles));

      yield call(updateBundleSoundcastsOrder, soundcastId, orderedIds);
    }
  } catch (error) {
    console.log(`ERROR: update bundle soundcasts order error: ${error.message}`);
  }
};

const updateBundleSoundcastsOrderWatcher = function* () {
  yield takeLatest(updateManagedBundleSouncastsOrder.TRIGGER, updateBundleSoundcastsOrderWorker);
};

const fetchSoundcastsOrderWorker = function* ({ payload }) {
  const { publisherID } = payload;
  try {
    yield put(fetchManagedSoundcastsOrder.request(payload));
    yield put(changeSoundcastsOrder({ busy: true, loaded: false }));

    const data = yield yield call(getSoundcastsOrder, publisherID, 'individuals');
    const orderedIds = data.map(value => value.soundcastId);

    yield put(changeSoundcastsOrder({ orderedIds }));

    yield put(fetchManagedSoundcastsOrder.success(payload));
  } catch (error) {
    yield put(fetchManagedSoundcastsOrder.failure(payload, error));
  } finally {
    yield put(changeSoundcastsOrder({ busy: false, loaded: true }));
    yield put(fetchManagedSoundcastsOrder.fulfill(payload));
  }
};

const fetchSoundcastsOrderWatcher = function* () {
  yield takeLatest(fetchManagedSoundcastsOrder.TRIGGER, fetchSoundcastsOrderWorker);
};

const fetchBundlesOrderWorker = function* ({ payload }) {
  const { publisherID } = payload;
  try {
    yield put(fetchManagedBundlesOrder.request(payload));
    yield put(changeBundlesOrder({ busy: true, loaded: false }));

    const data = yield yield call(getSoundcastsOrder, publisherID, 'bundles');
    const orderedIds = data.map(value => value.soundcastId);

    yield put(changeBundlesOrder({ orderedIds }));

    yield put(fetchManagedBundlesOrder.success(payload));
  } catch (error) {
    yield put(fetchManagedBundlesOrder.failure(payload, error));
  } finally {
    yield put(changeBundlesOrder({ busy: false, loaded: true }));
    yield put(fetchManagedBundlesOrder.fulfill(payload));
  }
};

const fetchBundlesOrderWatcher = function* () {
  yield takeLatest(fetchManagedBundlesOrder.TRIGGER, fetchBundlesOrderWorker);
};

const fetchBundleSoundcastsOrderWorker = function* ({ payload }) {
  const { soundcastId } = payload;
  let orderedIds = [];
  try {
    yield put(fetchManagedBundleSoundcastsOrder.request(payload));

    const data = yield yield call(getBundleSoundcastsOrder, soundcastId);
    orderedIds = data.map(value => value.soundcastId);

    yield put(fetchManagedBundleSoundcastsOrder.success(payload));
  } catch (error) {
    yield put(fetchManagedBundleSoundcastsOrder.failure(payload, error));
  } finally {
    const orderedInBundles = {};
    orderedInBundles[soundcastId] = orderedIds;
    yield put(changeBundleSoundcastsOrder(orderedInBundles));

    yield put(fetchManagedBundleSoundcastsOrder.fulfill(payload));
  }
};

const fetchBundleSoundcastsOrderWatcher = function* () {
  yield takeLatest(fetchManagedBundleSoundcastsOrder.TRIGGER, fetchBundleSoundcastsOrderWorker);
};

export default function* () {
  yield all([
    call(soundcastUpdateWatcher),
    call(fetchSoundcastEpisodesWatcher),
    call(fetchSoundcastRatingsListWatcher),
    call(fetchSoundcastRatingsOverviewWatcher),
    call(deletedSoundcastRatingsWatcher),
    call(updateSoundcastsOrderWatcher),
    call(updateBundlesOrderWatcher),
    call(updateBundleSoundcastsOrderWatcher),
    call(fetchSoundcastsOrderWatcher),
    call(fetchBundlesOrderWatcher),
    call(fetchBundleSoundcastsOrderWatcher),
  ]);
}
